import { matchRanges } from '@app/studio'
import { useEffect, useMemo, useRef, useState } from 'react'
import { barClearance } from './bar-clearance'
import { Caret } from './Caret'
import { Matched } from './Matched'
import './contents.css'

export type Entry = {
  id: string
  label: string
  /**
   * What the book CALLS this part — "Chapter Three" — where it calls it
   * anything. Set above the title, the way the printed contents sets it: the
   * name is how a reader refers to a part, and the title is what it is about.
   */
  name?: string
  /**
   * The parts under this one. A work whose parts have no parts of their own —
   * a source's segments — gives none, and the rail is a flat list; a book gives
   * a tree and the rail nests it.
   */
  children?: Entry[]
}

/**
 * A work's parts, listed beside it.
 *
 * **Only where the parts have names.** A work whose passages are numbered
 * paragraphs has nothing to list — a rail of a hundred and ninety-five figures
 * is the same information the margin already carries, taking a third of the
 * width to repeat it. Where the parts are named, the names are the only way to
 * reach a place in a long work without scrolling for it.
 *
 * So this renders nothing at all rather than degrading: an empty rail teaches a
 * reader to ignore the space where a useful one would be.
 */
export function Contents({
  entries,
  onGo,
  query = '',
  opens,
}: {
  entries: Entry[]
  /**
   * The control that stands for this list where the surface hides it.
   *
   * A rail that gives way below an edge takes every entry out of reach unless
   * something else opens onto them, and a surface that hides it is the one that
   * knows what does. Named by the word that control reads as, which is how it
   * is found; absent where the list is never hidden.
   */
  opens?: string
  /** Called with the id to move to; the page owns the scrolling. */
  onGo: (id: string) => void
  /**
   * What was searched for, where the surface offers searching at all.
   *
   * The field belongs to the band that draws it, not to this list: a rail beside
   * a text has no room for one, and a list that built its own would be a second
   * field at a second height the moment it was shown anywhere else.
   */
  query?: string
}) {
  const here = useHere(entries)
  const ref = useRef<HTMLElement>(null)
  /*
   * Which branches somebody opened or shut by hand, and nothing more.
   *
   * Held as overrides rather than as the whole open/shut state, because the
   * reading already decides it: the branch being read is open. Stored whole,
   * the two answers would fight — arriving in a chapter would have to write
   * state, and closing a branch you were reading would reopen on the next
   * scroll tick.
   */
  const [decided, setDecided] = useState<Map<string, boolean>>(new Map())

  /** The branch the reading is inside — every ancestor of where the reader is. */
  const inside = useMemo(() => branchTo(entries, here), [entries, here])

  /** What the query leaves: what matched, and every part above it. */
  const shown = useMemo(() => narrow(entries, query), [entries, query])
  /**
   * The branches a query opens: those holding a match BELOW them.
   *
   * A part that matched on its own name is not one of them. Opened as well, a
   * search for a chapter's title answers with that chapter and everything
   * inside it — the whole subtree, none of which was asked for — so the result
   * is longest for the query that found exactly one thing.
   */
  const found = useMemo(() => branchesHolding(shown, query), [shown, query])

  /*
   * A branch is open where the reading is inside it, or where somebody said so.
   * While a query narrows the list that is set aside: a part shut over a match
   * is a search that found something and hid it, and one opened over none is a
   * subtree nobody asked for.
   */
  const open = (id: string) =>
    query.trim() ? found.has(id) : (decided.get(id) ?? inside.has(id))
  const toggle = (id: string) =>
    setDecided((held) => new Map(held).set(id, !open(id)))

  /* The rail follows the reading, or a long contents scrolls away from the
     entry it is marking and stops answering "where am I". */
  useEffect(() => {
    if (!here) return
    ref.current
      ?.querySelector(`[data-entry="${CSS.escape(here)}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [here])

  if (!entries.length) return null

  return (
    <nav className="toc" aria-label="Contents" data-opens={opens} ref={ref}>
      {shown.length ? (
        <Level
          entries={shown}
          here={here}
          isOpen={open}
          onToggle={toggle}
          onGo={onGo}
          query={query}
        />
      ) : (
        /* What the field did, said where the parts would be: a rail that is
           empty because of a query looks like a book with no parts. */
        <p className="comp__empty">Nothing matches “{query}”.</p>
      )}
    </nav>
  )
}

/**
 * The contents, narrowed to a query.
 *
 * A part is kept where its own name matches OR something under it does: a
 * chapter is how a reader reaches the section inside it, so dropping the
 * ancestors of a match leaves the match unreachable and says the book has no
 * such chapter.
 *
 * Matched on the LETTERS, the same way the marking is made, so a name carrying
 * vowels the query did not still both matches and shows what matched. Two
 * answers to "does this match" is two ways for one search to behave.
 */
function narrow(entries: Entry[], query: string): Entry[] {
  if (!query.trim()) return entries
  const keep = (e: Entry): Entry | null => {
    const under = (e.children ?? []).map(keep).filter((c): c is Entry => !!c)
    /* A part that matched and holds nothing else that did keeps everything
       under it, unfiltered — the reader found the chapter, and what is in it is
       what a chapter is. It stays shut until they open it, so the answer is as
       short as the question. Where something below matched too, that is what
       they were looking for and the rest gives way to it. */
    if (matches(e, query)) {
      return under.length
        ? { ...e, children: under }
        : { ...e, children: e.children }
    }
    return under.length ? { ...e, children: under } : null
  }
  return entries.map(keep).filter((e): e is Entry => !!e)
}

/** Whether a part's own name answers the query. */
function matches(e: Entry, query: string): boolean {
  const hits = (text: string) => matchRanges(text, query).length > 0
  return hits(e.label) || (!!e.name && hits(e.name))
}

/**
 * Every part with a match somewhere BELOW it — the branches a query opens.
 *
 * Its own match does not count: a chapter found by its title is an answer
 * complete in itself, and opening it as well buries that answer under the
 * subtree the reader did not ask about.
 */
function branchesHolding(entries: Entry[], query: string): Set<string> {
  const path = new Set<string>()
  if (!query.trim()) return path
  const walk = (e: Entry): boolean => {
    /* Every child is walked before the answer is taken, so a part deep in one
       branch does not stop the parts beside it from opening theirs. */
    const below = (e.children ?? []).map(walk).some(Boolean)
    if (below) path.add(e.id)
    return below || matches(e, query)
  }
  for (const e of entries) walk(e)
  return path
}

/**
 * Every entry on the way down to where the reader is, itself included.
 *
 * What it answers is which branches must be open, so it is the PATH rather than
 * the entry: an entry three levels down is unreachable on screen unless
 * everything above it is open too.
 */
function branchTo(entries: Entry[], here: string | null): Set<string> {
  const path = new Set<string>()
  if (!here) return path
  const walk = (list: Entry[], above: string[]): boolean =>
    list.some((e) => {
      if (e.id === here || walk(e.children ?? [], [...above, e.id])) {
        for (const id of [...above, e.id]) path.add(id)
        return true
      }
      return false
    })
  walk(entries, [])
  return path
}

/**
 * One level of the contents, and the levels under it.
 *
 * Nested lists rather than a flat list carrying an indent class: the nesting IS
 * the structure, so a screen reader gets it from the markup and the indent
 * follows from where a list sits rather than from a number somebody set.
 */
function Level({
  entries,
  here,
  isOpen,
  onToggle,
  onGo,
  query,
}: {
  entries: Entry[]
  here: string | null
  isOpen: (id: string) => boolean
  onToggle: (id: string) => void
  onGo: (id: string) => void
  /** What was searched for, so each name picks out what matched in it. */
  query: string
}) {
  return (
    <ul className="toc__list">
      {entries.map((e) => {
        const parts = e.children?.length ? e.children : null
        const shown = !!parts && isOpen(e.id)
        return (
          <li key={e.id}>
            <div className="toc__row">
              <button
                type="button"
                className="toc__entry"
                data-entry={e.id}
                {...(e.name ? { 'data-named': '' } : {})}
                {...(e.id === here ? { 'data-here': '' } : {})}
                aria-current={e.id === here ? 'true' : undefined}
                onClick={() => onGo(e.id)}
              >
                {/* The entry keeps the rail's alignment, and each line inside it
                    reads in its own direction: the words are an isolated run
                    INSIDE the block, so a Hebrew title runs right to left
                    without moving its row to the other edge, as on the printed
                    contents. A block taking the direction itself would align to
                    its own start, which is the other edge. */}
                {e.name ? (
                  <span className="toc__name">
                    <span dir="auto">
                      <Matched text={e.name} query={query} />
                    </span>
                  </span>
                ) : null}
                {/* The title is a part of its own rather than a bare string:
                    where there is room it takes a column, and every title lines
                    up whether or not the book names the part above it. */}
                <span className="toc__title">
                  <span dir="auto">
                    <Matched text={e.label} query={query} />
                  </span>
                </span>
              </button>
              {/*
                Opening a part and going to it are two intents, so they are two
                controls: a reader looking ahead at what a chapter holds has not
                asked to leave the page they are on.
                It TRAILS, which is what lets it be drawn only where there is
                something under it. Leading, an entry with no parts needs a
                spacer to keep the names aligned, and that spacer costs every
                name in the rail a caret's width — the same reasoning that puts
                a comment's actions at the trailing edge of its byline.
              */}
              {parts ? (
                <Caret
                  expanded={shown}
                  label={`${shown ? 'Hide' : 'Show'} what is in ${e.label}`}
                  onToggle={() => onToggle(e.id)}
                />
              ) : null}
            </div>
            {shown ? (
              <Level
                entries={parts}
                here={here}
                isOpen={isOpen}
                onToggle={onToggle}
                onGo={onGo}
                query={query}
              />
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Which part the reader is in.
 *
 * **The last part they have PASSED, not the one inside a band.** A band across
 * the upper window answers only while something happens to be crossing it, so a
 * short heading stops counting the moment it scrolls by and the rail falls back
 * to the chapter — or to nothing at all, between one heading and the next. What
 * a reader means by "where I am" is the last heading they went past, and it
 * holds until they pass another.
 *
 * The reading line is the foot of the fixed bar, so the answer is whatever the
 * chrome is currently sitting on top of rather than an arbitrary fraction of
 * the window.
 *
 * Measured from the rects rather than tracked by an observer: an observer
 * reports only what crosses it, so nothing is said about the parts above and
 * below — which is exactly what has to be compared to find the last one passed.
 * Read on a frame, and only when the page has actually moved.
 */
function useHere(entries: Entry[]): string | null {
  const [here, setHere] = useState<string | null>(null)

  useEffect(() => {
    const flat = flatten(entries)
    if (!flat.length) return

    const targets = flat
      .map((e) => ({
        id: e.id,
        el: document.querySelector(`[data-passage="${CSS.escape(e.id)}"]`),
      }))
      .filter((t): t is { id: string; el: Element } => !!t.el)
    if (!targets.length) return

    let queued = false
    const read = () => {
      queued = false
      const line = barClearance()
      /* In reading order a part always begins above the next one, so the last
         one still above the line is the one being read — and the scan can stop
         at the first that is below it. */
      let reached: string | null = null
      for (const t of targets) {
        if (t.el.getBoundingClientRect().top - line > 1) break
        reached = t.id
      }
      /* Above the first part — the title page of a long book — the first part
         is still the one being approached, and an unmarked rail says the rail
         is broken rather than that the reader is early. */
      setHere(reached ?? targets[0].id)
    }

    const onScroll = () => {
      if (queued) return
      queued = true
      requestAnimationFrame(read)
    }

    read()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [entries])

  return here
}

/** Every entry in reading order, whatever level it sits at. */
function flatten(entries: Entry[]): Entry[] {
  return entries.flatMap((e) => [e, ...flatten(e.children ?? [])])
}
